import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Label,
  ComposedChart,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content || typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");
  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;
        if (trimmed.startsWith("### ")) {
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        }
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }
        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

// ───────────────────── Chart Helpers ─────────────────────
const yAxisLabel = "kcal / cap / day";
const yAxisDx = -yAxisLabel.length;

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg
      className="animate-spin h-5 w-5 text-[#59AD31]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      ></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function getOrderByMaxValue(data, keys) {
  if (!data || !Array.isArray(data) || data.length === 0) return keys;
  return [...keys].sort((a, b) => {
    const maxA = Math.max(
      ...data.map((d) => (typeof d[a] === "number" ? d[a] : 0)),
    );
    const maxB = Math.max(
      ...data.map((d) => (typeof d[b] === "number" ? d[b] : 0)),
    );
    return maxB - maxA;
  });
}

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;
  if (key && !key.startsWith("kcalTarget") && (key.includes("Target") || key.includes("target"))) {
    return (
      <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
        <line x1="0" y1="1.5" x2="16" y2="1.5" stroke={entry.color} strokeWidth="2" strokeDasharray="3,2" />
      </svg>
    );
  }
  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }
  return (
    <span
      className="h-3 w-3 rounded-[2px]"
      style={{
        backgroundColor: entry.color === "white" ? "#ffffff" : entry.color,
        border: entry.color === "white" ? "1px solid #323334" : "none",
      }}
    />
  );
}

function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;
  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });
  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item, idx) => {
        return (
          <div
            key={`${item.dataKey}-${idx}`}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {getTooltipIcon(item, chartType)}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabel}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;
  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey),
  );
  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const isTarget = entry.dataKey && !entry.dataKey.startsWith("kcalTarget") && (entry.dataKey.includes("Target") || entry.dataKey.includes("target"));
        let entryChartType = chartType;
        if (chartType === "bar") {
          if (entry.dataKey?.includes("Target") && !entry.dataKey.startsWith("kcalTarget") || entry.dataKey === "mder") {
            entryChartType = "line";
          }
        }
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {entry.type === "circle" ? (
              <span
                className="h-3 w-3 rounded-full"
                style={{
                  backgroundColor:
                    entry.color === "white" ? "#ffffff" : entry.color,
                  border: "1px solid #323334",
                }}
              />
            ) : entryChartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line
                  x1="0" y1="1" x2="20" y2="1"
                  stroke={entry.color}
                  strokeWidth="2"
                  strokeDasharray={isTarget ? "3,2" : "0"}
                />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

const COUNTRY_COLORS = [
  "#0B2C6B",
  "#A6A6A6",
  "#1FA04A",
  "#080c0eff",
  "#E0B000",
  "#B45309",
  "#D946EF",
  "#0F766E",
  "#F97316",
  "#64748B",
];

function getCountryColor(index) {
  return COUNTRY_COLORS[index % COUNTRY_COLORS.length];
}

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }
  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }
  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }
  return "Could not load analysis at this time.";
}

function resolvePathwayNode(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;
  if (Array.isArray(inputData.charts)) return inputData;
  if (pathway && inputData[pathway] && typeof inputData[pathway] === "object") {
    return inputData[pathway];
  }
  const keys = Object.keys(inputData);
  if (keys.length === 1 && typeof inputData[keys[0]] === "object") {
    const candidate = inputData[keys[0]];
    if (Array.isArray(candidate?.charts)) return candidate;
  }
  return null;
}

// ───────────────────── Component ─────────────────────

export default function FoodTab({ data, pathway, pathwayId, scenathonId }) {
  const resolvedPathwayNode = useMemo(
    () => resolvePathwayNode(data, pathway),
    [data, pathway],
  );

  const countries = Array.isArray(data?.countries) ? data.countries : [];

  const comparableCountries = useMemo(() => {
    return countries.filter((c) => c !== "Global");
  }, [countries]);

  const defaultCountry = useMemo(() => {
    if (countries.includes("Global")) return "Global";
    return countries[0] || "";
  }, [countries]);

  const [selectedIntakeCountry, setSelectedIntakeCountry] = useState(defaultCountry);
  const [selectedGroupCountry, setSelectedGroupCountry] = useState(defaultCountry);
  const [hiddenComparisonKeys, setHiddenComparisonKeys] = useState([]);
  const [hiddenIntakeKeys, setHiddenIntakeKeys] = useState([]);
  const [hiddenGroupKeys, setHiddenGroupKeys] = useState([]);
  const [hiddenByCountryKeys, setHiddenByCountryKeys] = useState([]);
  const [hiddenPouKeys, setHiddenPouKeys] = useState([]);
  const [hiddenPouEvolutionKeys, setHiddenPouEvolutionKeys] = useState([]);

  const [comparisonAnalysis, setComparisonAnalysis] = useState("");
  const [intakeAnalysis, setIntakeAnalysis] = useState("");
  const [groupAnalysis, setGroupAnalysis] = useState("");
  const [loadingComparisonAnalysis, setLoadingComparisonAnalysis] = useState(false);
  const [loadingIntakeAnalysis, setLoadingIntakeAnalysis] = useState(false);
  const [loadingGroupAnalysis, setLoadingGroupAnalysis] = useState(false);

  useEffect(() => {
    if (!selectedIntakeCountry && defaultCountry) {
      setSelectedIntakeCountry(defaultCountry);
    } else if (
      selectedIntakeCountry &&
      countries.length > 0 &&
      !countries.includes(selectedIntakeCountry)
    ) {
      setSelectedIntakeCountry(defaultCountry);
    }
    if (!selectedGroupCountry && defaultCountry) {
      setSelectedGroupCountry(defaultCountry);
    } else if (
      selectedGroupCountry &&
      countries.length > 0 &&
      !countries.includes(selectedGroupCountry)
    ) {
      setSelectedGroupCountry(defaultCountry);
    }
  }, [countries, defaultCountry, selectedIntakeCountry, selectedGroupCountry]);

  const intakeData = useMemo(() => {
    return data?.["Daily per capita food intake"]?.[selectedIntakeCountry] || [];
  }, [data, selectedIntakeCountry]);

  const groupData = useMemo(() => {
    return data?.["Group food intake"]?.[selectedGroupCountry] || [];
  }, [data, selectedGroupCountry]);

  const comparisonData = useMemo(() => {
    const source = data?.["Daily per capita food intake"] || {};
    const yearsSet = new Set();
    comparableCountries.forEach((country) => {
      (source[country] || []).forEach((row) => {
        const y = Number(row.year);
        if (Number.isFinite(y)) yearsSet.add(y);
      });
    });
    const years = Array.from(yearsSet).sort((a, b) => a - b);
    if (years.length === 0) return [];
    return years.map((year) => {
      const row = { year };
      comparableCountries.forEach((country) => {
        const found = (source[country] || []).find(
          (r) => Number(r.year) === year,
        );
        row[`feasible_${country}`] = Number(found?.feasibleKcal) || 0;
        if (row.minTarget == null && found?.minTarget != null) {
          row.minTarget = Number(found.minTarget) || 0;
        }
        if (row.maxTarget == null && found?.maxTarget != null) {
          row.maxTarget = Number(found.maxTarget) || 0;
        }
      });
      row.minTarget = Number(row.minTarget) || 0;
      row.maxTarget = Number(row.maxTarget) || 0;
      return row;
    });
  }, [data, comparableCountries]);

  // New data structure: Daily per capita food consumption by country (2030 & 2050)
  const byCountryData = useMemo(() => {
    const source = data?.["Daily per capita food intake"] || {};
    const yearsToShow = [2030, 2050];
    
    return comparableCountries.map((country) => {
      const countryData = source[country] || [];
      const row = { country };
      
      yearsToShow.forEach((year) => {
        const found = countryData.find((r) => Number(r.year) === year);
        row[`feasible_${year}`] = Number(found?.feasibleKcal) || 0;
        row[`kcalTarget_${year}`] = Number(found?.kcalTarget) || 0;
        row[`minTarget_${year}`] = Number(found?.minTarget) || 0;
        row[`maxTarget_${year}`] = Number(found?.maxTarget) || 0;
      });
      
      return row;
    });
  }, [data, comparableCountries]);

  // Data structure: Prevalence of undernourishment by country (2030 & 2050)
  const pouData = useMemo(() => {
    const source = data?.["Prevalence of undernourishment"] || {};
    const yearsToShow = [2030, 2050];
    
    return comparableCountries.map((country) => {
      const countryData = source[country] || [];
      const row = { country };
      
      yearsToShow.forEach((year) => {
        const found = countryData.find((r) => Number(r.year) === year);
        row[`pou_${year}`] = Number(found?.pou) || 0;
      });
      
      return row;
    });
  }, [data, comparableCountries]);

  // Evolution data: Prevalence of undernourishment evolution by country
  const pouEvolutionData = useMemo(() => {
    const source = data?.["Prevalence of undernourishment"] || {};
    const yearsSet = new Set();
    comparableCountries.forEach((country) => {
      (source[country] || []).forEach((row) => {
        const y = Number(row.year);
        if (Number.isFinite(y)) yearsSet.add(y);
      });
    });
    const years = Array.from(yearsSet).sort((a, b) => a - b);
    if (years.length === 0) return [];
    return years.map((year) => {
      const row = { year };
      comparableCountries.forEach((country) => {
        const found = (source[country] || []).find(
          (r) => Number(r.year) === year,
        );
        row[`pou_${country}`] = Number(found?.pou) || 0;
      });
      return row;
    });
  }, [data, comparableCountries]);

  const countryOptions = useMemo(
    () => countries.map((country) => ({ value: country, label: country })),
    [countries],
  );

  const comparisonLegendOrder = useMemo(() => {
    return [
      ...comparableCountries.map((country) => `feasible_${country}`),
      "maxTarget",
      "minTarget",
    ];
  }, [comparableCountries]);

  const comparisonLegendPayload = useMemo(() => {
    return [
      ...comparableCountries.map((country, idx) => ({
        value: country,
        type: "line",
        id: `feasible_${country}`,
        dataKey: `feasible_${country}`,
        color: getCountryColor(idx),
      })),
      // {
      //   value: "Max Target",
      //   type: "line",
      //   id: "maxTarget",
      //   dataKey: "maxTarget",
      //   color: "#FFD786",
      // },
      // {
      //   value: "Min Target",
      //   type: "line",
      //   id: "minTarget",
      //   dataKey: "minTarget",
      //   color: "#ff0000",
      // },
    ];
  }, [comparableCountries]);

  const intakeLegendOrder = useMemo(() => {
    return getOrderByMaxValue(intakeData, [
      "feasibleKcal",
      "kcalTarget",
      "mder",
      "minTarget",
      "maxTarget",
    ]);
  }, [intakeData]);

  const groupLegendOrder = useMemo(() => {
    return getOrderByMaxValue(groupData, [
      "cereals",
      "fish",
      "fruitsVeg",
      "milk",
      "nuts",
      "poultry",
      "redMeat",
      "sugar",
      "others",
    ]);
  }, [groupData]);

  // ── Comparison Analysis ──────────────────────────────────────────────────
  useEffect(() => {
    if (!scenathonId || !pathway) {
      setComparisonAnalysis("");
      setLoadingComparisonAnalysis(false);
      return;
    }
    if (comparisonData.length === 0) {
      setComparisonAnalysis("");
      setLoadingComparisonAnalysis(false);
      return;
    }
    const controller = new AbortController();
    const fetchComparisonAnalysis = async () => {
      setLoadingComparisonAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              analysisKey: "intake_data_scenathon_result_analysis",
              scope: "pathway",
              scenathonId,
              pathway,
              intakeScenathonData: {
                data: JSON.stringify(comparisonData),
                title: "Daily per capita food consumption comparison",
              },
              context: "food",
            }),
            signal: controller.signal,
          },
        );
        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }
        const resData = await response.json();
        setComparisonAnalysis(
          extractAnalysisText(resData, [
            "intakeDataScenathonResultAnalysis",
            "analysis",
          ]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching comparison analysis:", error);
          setComparisonAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingComparisonAnalysis(false);
        }
      }
    };
    fetchComparisonAnalysis();
    return () => controller.abort();
  }, [comparisonData, pathway, scenathonId]);

  // ── Intake Analysis ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!scenathonId || !pathway || !selectedIntakeCountry || intakeData.length === 0) {
      setIntakeAnalysis("");
      setLoadingIntakeAnalysis(false);
      return;
    }
    const controller = new AbortController();
    const fetchIntakeAnalysis = async () => {
      setLoadingIntakeAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              analysisKey: "intake_data_analysis",
              scope: "country",
              scenathonId,
              pathway,
              country: selectedIntakeCountry,
              intakeData: {
                data: JSON.stringify(intakeData),
                title: `Daily per capita food intake - ${selectedIntakeCountry}`,
              },
              context: "food",
            }),
            signal: controller.signal,
          },
        );
        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }
        const resData = await response.json();
        setIntakeAnalysis(
          extractAnalysisText(resData, ["intakeDataAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching intake analysis:", error);
          setIntakeAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingIntakeAnalysis(false);
        }
      }
    };
    fetchIntakeAnalysis();
    return () => controller.abort();
  }, [intakeData, pathway, scenathonId, selectedIntakeCountry]);

  // ── Group Analysis ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!scenathonId || !pathway || !selectedGroupCountry || groupData.length === 0) {
      setGroupAnalysis("");
      setLoadingGroupAnalysis(false);
      return;
    }
    const controller = new AbortController();
    const fetchGroupAnalysis = async () => {
      setLoadingGroupAnalysis(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              analysisKey: "group_data_analysis",
              scope: "country",
              scenathonId,
              pathway,
              country: selectedGroupCountry,
              groupData: {
                data: JSON.stringify(groupData),
                title: `Group food intake - ${selectedGroupCountry}`,
              },
              context: "food",
            }),
            signal: controller.signal,
          },
        );
        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }
        const resData = await response.json();
        setGroupAnalysis(
          extractAnalysisText(resData, ["groupDataAnalysis", "analysis"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching group analysis:", error);
          setGroupAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingGroupAnalysis(false);
        }
      }
    };
    fetchGroupAnalysis();
    return () => controller.abort();
  }, [groupData, pathway, scenathonId, selectedGroupCountry]);

  // ── Legend toggle handlers ───────────────────────────────────────────────
  const handleLegendClickComparison = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenComparisonKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  const handleLegendClickIntake = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenIntakeKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  const handleLegendClickGroup = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenGroupKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  const handleLegendClickByCountry = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenByCountryKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  const handleLegendClickPou = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenPouKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  const handleLegendClickPouEvolution = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenPouEvolutionKeys((prev) =>
      prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey],
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex w-full flex-col gap-10">

      {/* BLOCK 1 — Daily per capita food consumption (per country) */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Daily per capita food consumption
          </h2>
          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedIntakeCountry}
              onChange={setSelectedIntakeCountry}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>
        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-4 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={intakeData}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={50}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value={yAxisLabel}
                  angle={-90}
                  position="left"
                  dx={yAxisDx}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={intakeLegendOrder}
                    hiddenKeys={hiddenIntakeKeys}
                    onToggle={handleLegendClickIntake}
                    chartType="bar"
                  />
                )}
              />
              <Bar
                dataKey="feasibleKcal"
                name="Feasible"
                fill="#97E6BA"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenIntakeKeys.includes("feasibleKcal")}
              />

              <Bar
                dataKey="kcalTarget"
                name="Kcal Target"
                fill="#6da7ff"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenIntakeKeys.includes("kcalTarget")}
              />

              <Line
                type="monotone"
                dataKey="minTarget"
                name="Min Target"
                stroke="#ff0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenIntakeKeys.includes("minTarget")}
              />
              <Line
                type="monotone"
                dataKey="maxTarget"
                name="Max Target"
                stroke="#FFD786"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenIntakeKeys.includes("maxTarget")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* <div className="w-full min-h-[100px]">
          {loadingIntakeAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={intakeAnalysis} />
          )}
        </div> */}
      </section>

      {/* BLOCK 2 — Cross-country comparison */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Daily per capita food consumption comparison
        </h2>
        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={Array.isArray(comparisonData) ? comparisonData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value={yAxisLabel}
                  angle={-90}
                  position="left"
                  dx={yAxisDx}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="line" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={() => (
                  <OrderedLegend
                    payload={comparisonLegendPayload}
                    order={comparisonLegendOrder}
                    hiddenKeys={hiddenComparisonKeys}
                    onToggle={handleLegendClickComparison}
                    chartType="line"
                  />
                )}
              />
              {comparableCountries.map((country, idx) => (
                <Line
                  key={`feasible_${country}`}
                  type="monotone"
                  dataKey={`feasible_${country}`}
                  name={country}
                  stroke={getCountryColor(idx)}
                  strokeWidth={2}
                  dot={false}
                  hide={hiddenComparisonKeys.includes(`feasible_${country}`)}
                />
              ))}
              {/* <Line
                type="monotone"
                dataKey="maxTarget"
                name="Max Target"
                stroke="#FFD786"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenComparisonKeys.includes("maxTarget")}
              />
              <Line
                type="monotone"
                dataKey="minTarget"
                name="Min Target"
                stroke="#ff0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenComparisonKeys.includes("minTarget")}
              /> */}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* <div className="w-full min-h-[100px]">
          {loadingComparisonAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={comparisonAnalysis} />
          )}
        </div> */}
      </section>

      {/* BLOCK 3 — Food group breakdown */}
      {/* <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Daily per capita food consumption by group
          </h2>
          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedGroupCountry}
              onChange={setSelectedGroupCountry}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>
        <div className="h-[700px] rounded-xl border border-[#EDF0F2] px-4 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={groupData}
              stackOffset="none"
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={50}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value={yAxisLabel}
                  angle={-90}
                  position="left"
                  dx={yAxisDx}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={groupLegendOrder}
                    hiddenKeys={hiddenGroupKeys}
                    onToggle={handleLegendClickGroup}
                    chartType="bar"
                  />
                )}
              />
              <Bar dataKey="cereals" stackId="a" fill="#FFD786" name="Cereals" hide={hiddenGroupKeys.includes("cereals")} />
              <Bar dataKey="fish" stackId="a" fill="#9B59B6" name="Fish" hide={hiddenGroupKeys.includes("fish")} />
              <Bar dataKey="fruitsVeg" stackId="a" fill="#EAC65B" name="Fruits&vegs" hide={hiddenGroupKeys.includes("fruitsVeg")} />
              <Bar dataKey="milk" stackId="a" fill="#FB752E" name="Milk" hide={hiddenGroupKeys.includes("milk")} />
              <Bar dataKey="nuts" stackId="a" fill="#FFDDDB" name="Nuts" hide={hiddenGroupKeys.includes("nuts")} />
              <Bar dataKey="poultry" stackId="a" fill="#FF5579" name="Poultry" hide={hiddenGroupKeys.includes("poultry")} />
              <Bar dataKey="redMeat" stackId="a" fill="#009B8A" name="Red meat" hide={hiddenGroupKeys.includes("redMeat")} />
              <Bar dataKey="sugar" stackId="a" fill="#80D5FF" name="Sugar" hide={hiddenGroupKeys.includes("sugar")} />
              <Bar dataKey="others" stackId="a" fill="#E3E5E6" name="Others" hide={hiddenGroupKeys.includes("others")} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* <div className="w-full min-h-[100px]">
          {loadingGroupAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={groupAnalysis} />
          )}
        </div> */}
      {/* </section> */}

      {/* BLOCK 4 — Daily per capita food consumption by country (2030 & 2050) */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Daily per capita food consumption by country or region 
        </h2>
        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={byCountryData}
              margin={{ top: 40, right: 30, left: 60, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="country"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Countries"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={50}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value={yAxisLabel}
                  angle={-90}
                  position="left"
                  dx={yAxisDx}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={["feasible_2030", "kcalTarget_2030", "feasible_2050", "kcalTarget_2050", "minTarget_2030", "maxTarget_2030"]}
                    hiddenKeys={hiddenByCountryKeys}
                    onToggle={handleLegendClickByCountry}
                    chartType="bar"
                  />
                )}
              />
              <Bar
                dataKey="feasible_2030"
                name="Feasible 2030"
                fill="#97E6BA"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenByCountryKeys.includes("feasible_2030")}
              />
              <Bar
                dataKey="kcalTarget_2030"
                name="Kcal Target 2030"
                fill="#6da7ff"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenByCountryKeys.includes("kcalTarget_2030")}
              />
              <Bar
                dataKey="feasible_2050"
                name="Feasible 2050"
                fill="#1FA04A"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenByCountryKeys.includes("feasible_2050")}
              />
              <Bar
                dataKey="kcalTarget_2050"
                name="Kcal Target 2050"
                fill="#0B3D91"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenByCountryKeys.includes("kcalTarget_2050")}
              />
              <Line
                type="monotone"
                dataKey="minTarget_2030"
                name="Min Target 2030"
                stroke="#ff0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenByCountryKeys.includes("minTarget_2030")}
              />
              <Line
                type="monotone"
                dataKey="maxTarget_2030"
                name="Max Target 2030"
                stroke="#FFD786"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenByCountryKeys.includes("maxTarget_2030")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* BLOCK 5 — Prevalence of undernourishment by country (2030 & 2050) */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Prevalence of undernourishment by country or region 
        </h2>
        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={pouData}
              margin={{ top: 40, right: 30, left: 60, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="country"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Countries"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={50}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value="% of population"
                  angle={-90}
                  position="left"
                  dx={-35}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.03)" }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={["pou_2030", "pou_2050"]}
                    hiddenKeys={hiddenPouKeys}
                    onToggle={handleLegendClickPou}
                    chartType="bar"
                  />
                )}
              />
              <Bar
                dataKey="pou_2030"
                name="PoU 2030"
                fill="#F59E0B"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenPouKeys.includes("pou_2030")}
              />
              <Bar
                dataKey="pou_2050"
                name="PoU 2050"
                fill="#DC2626"
                opacity={0.8}
                radius={[4, 4, 0, 0]}
                hide={hiddenPouKeys.includes("pou_2050")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* BLOCK 6 — Prevalence of undernourishment evolution */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Prevalence of undernourishment evolution
        </h2>
        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={Array.isArray(pouEvolutionData) ? pouEvolutionData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })
                }
              >
                <Label
                  value="% of population"
                  angle={-90}
                  position="left"
                  dx={-35}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip
                content={(props) => <CustomTooltip {...props} chartType="line" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ top: 0, left: 0 }}
                content={(props) => (
                  <OrderedLegend
                    {...props}
                    order={comparableCountries.map((country) => `pou_${country}`)}
                    hiddenKeys={hiddenPouEvolutionKeys}
                    onToggle={handleLegendClickPouEvolution}
                    chartType="line"
                  />
                )}
              />
              {comparableCountries.map((country, idx) => (
                <Line
                  key={`pou_${country}`}
                  type="monotone"
                  dataKey={`pou_${country}`}
                  name={country}
                  stroke={getCountryColor(idx)}
                  strokeWidth={2}
                  dot={false}
                  hide={hiddenPouEvolutionKeys.includes(`pou_${country}`)}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

    </div>
  );
}